import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './Button';
import { EmptyState } from './States';

/**
 * Last line of defence: a render error anywhere below would otherwise leave a
 * blank white screen. Shows a recoverable message with a reload button.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Admin render error', error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-canvas)] p-6" role="alert">
        <EmptyState
          icon={AlertTriangle}
          title="Something went wrong"
          description="The admin hit an unexpected error. Reloading usually fixes it."
          action={
            <Button variant="primary" onClick={() => window.location.reload()}>
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Reload
            </Button>
          }
        />
      </div>
    );
  }
}
